import React, { Suspense, useEffect } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Route, Routes, useLocation, Navigate, useParams } from "react-router"
import GAMES from "./config/games"
import Header from "./components/layout/Header"
import { MapProvider } from "react-map-gl"
import { AuthProvider } from "./hooks/context/auth"
import theme, { GlobalStyle } from "./styles"
import { ThemeProvider } from "styled-components"
import { UiProvider } from "./hooks/context/ui"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { persistQueryClient } from "@tanstack/react-query-persist-client"
import { Toaster } from "react-hot-toast"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import ReactGA from "react-ga4"
import { MapDataProvider } from "./hooks/queries/use-map-data"
import GTMPageTracker from "./components/GTMPageTracker"
import { Analytics } from "@vercel/analytics/react"
import UnsavedTrackBanner from "./components/UnsavedTrackBanner"

// Lazy-loaded pages for code splitting
const Landing = React.lazy(() => import("./pages/Landing"))
const GameLanding = React.lazy(() => import("./pages/GameLanding"))
const GameMapWrapper = React.lazy(() => import("./pages/GameMapWrapper"))
const Account = React.lazy(() => import("./pages/auth/Account"))
const Terms = React.lazy(() => import("./pages/Terms"))
const Privacy = React.lazy(() => import("./pages/Privacy"))
const Subscribe = React.lazy(() => import("./pages/subscribe/Subscribe"))
const OauthCallback = React.lazy(() => import("./pages/auth/oauth/Callback"))
const Playground = React.lazy(() => import("./pages/Playground"))
const ReactQueryDevtools = React.lazy(() => import("@tanstack/react-query-devtools").then(m => ({ default: m.ReactQueryDevtools })))

// Lazy-loaded auth callbacks
const AuthConfirm = React.lazy(() => import("./pages/auth/AuthConfirm").then(m => ({ default: m.AuthConfirm })))
const AuthReset = React.lazy(() => import("./pages/auth/AuthResetPassword").then(m => ({ default: m.AuthReset })))
const SubscribeSuccessCallback = React.lazy(() => import("./pages/subscribe/SubscribeCallback").then(m => ({ default: m.SubscribeSuccessCallback })))
const SubscribeFailureCallback = React.lazy(() => import("./pages/subscribe/SubscribeCallback").then(m => ({ default: m.SubscribeFailureCallback })))

ReactGA.initialize(import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID)
/* React-query stuff */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
      gcTime: 1000 * 60 * 60, // 1 hour (changed from cacheTime, which is renamed in v5)
    },
  },
})

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "TANSTACK_QUERY_CACHE", // custom key for the cache in localStorage
})

// Setup persistQueryClient
persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: (import.meta.env.NODE_ENV === "development" ? 5 : 60 * 24) * 60 * 1000, // 24 hours,
  buster: import.meta.env.VITE_COMMIT_HASH,
})

// Useless redux stuff

// import { Provider as Redux } from "react-redux"
// import { store } from "./store/redux"
// import { PersistGate as ReduxPersist } from "redux-persist/integration/react"
// import { persistStore } from "redux-persist"
// const persistor = persistStore(store)
/*<Redux store={store}>*/
/*  <ReduxPersist loading={null} persistor={persistor}>*/
/*</ReduxPersist>*/
/*</Redux>*/

// Redirect legacy /map/:gameSlug URLs to new /:gameSlug pattern
function MapRedirect() {
  const { gameSlug } = useParams<{ gameSlug: string }>()
  return <Navigate to={`/${gameSlug}`} replace />
}

const App = () => {
  const location = useLocation()
  useEffect(() => {
    console.log(location)
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search })
  }, [location.pathname, location.search])

  return (
    <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_KEY} useRecaptchaNet>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
        <ThemeProvider theme={theme}>
          <UiProvider>
            <MapDataProvider>
              <GlobalStyle />
              <AuthProvider>
                  <UnsavedTrackBanner />
                  <MapProvider>
                  {(() => { const path = location.pathname; const staticPages = ['/', '/terms', '/privacy']; if (staticPages.includes(path)) return false; const parts = path.replace(/^\//, '').split('/'); return !(parts.length === 1 && parts[0] in GAMES); })() && <Header />}
                  <GTMPageTracker>
                    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#888' }}>Loading...</div>}>
                    <Routes>
                      <Route path="/" element={<Landing />} />
                      <Route path="/map" element={<Navigate to="/" />} />
                      <Route path="/map/:gameSlug" element={<MapRedirect />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/:gameSlug" element={<GameLanding />} />
                      <Route path="/:gameSlug/map" element={<GameMapWrapper />} />
                      <Route path="/:gameSlug/:mapSlug" element={<GameMapWrapper />} />

                      {/* auth callback pages */}
                      <Route path="/account" element={<Account />} />
                      <Route path="/auth/change-password/confirm" element={<AuthReset />} />

                      {/* Oauth / confirm callback pages */}
                      <Route path="/auth/confirm" element={<AuthConfirm />} />
                      <Route path="/oauth/callback/:provider" element={<OauthCallback />} />

                      {/* Subscription Pages */}
                      <Route path="/subscribe" element={<Subscribe />} />
                      <Route path="/subscribe/stripe/callback/success" element={<SubscribeSuccessCallback />} />
                      <Route path="/subscribe/stripe/callback/failure" element={<SubscribeFailureCallback />} />

                      {import.meta.env.NODE_ENV === "development" && (
                        <Route path="/playground" element={<Playground />} />
                      )}
                    </Routes>
                    </Suspense>
                  </GTMPageTracker>
                  </MapProvider>

              </AuthProvider>
            </MapDataProvider>
          </UiProvider>
        </ThemeProvider>
        <Analytics />
      </QueryClientProvider>
    </GoogleReCaptchaProvider>
  )
}

export default App
